import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Checkbox,
	Flex,
	FormControl,
	FormLabel,
	Grid,
	HStack,
	Icon,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Text,
	Textarea,
	useColorModeValue,
	useDisclosure,
	useToast,
	VStack,
	Wrap,
	WrapItem,
} from "@chakra-ui/react";
import moment from "moment";
import { FiBell, FiChevronDown, FiClock, FiCopy, FiHome, FiSearch, FiSend, FiUsers } from "react-icons/fi";

import TablePagination from "../../Components/Pagination/TablePagination";
import { selectActiveProperties, sendMassNotificationsAsync } from "../../redux/user/userSlice";
import { fullDateTimeFormat } from "../../utils/types";
import { getPropertyAddress } from "../../utils/verifier";

export default function MassNotification() {
	const textColor = useColorModeValue("gray.700", "white");
	const bgCard = useColorModeValue("white", "gray.700");
	const bgHover = useColorModeValue("gray.50", "gray.600");
	const borderColor = useColorModeValue("gray.200", "gray.600");
	const { t } = useTranslation();

	const ITEMS_PER_PAGE = 5;

	const initialNotificationInformation = {
		title: "",
		message: "",
		recipients: { type: "property", notifyOwner: false, propertyId: "" },
	};

	const [value, setValue] = useState("property");
	const [notificationInformation, setNotificationInformation] = useState(initialNotificationInformation);
	const [selectedNotification, setSelectedNotification] = useState(null);
	const [propertyNotificationsPage, setPropertyNotificationsPage] = useState(1);
	const [massNotificationsPage, setMassNotificationsPage] = useState(1);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const properties = useSelector(selectActiveProperties);
	const notifications = properties
		.map(
			(p) =>
				p.notifications?.map((n) => {
					return { ...n, address: getPropertyAddress(p) };
				}) || []
		)
		.flat();
	const massNotifications = useSelector((state) => state.user.user?.notifications);
	const dispatch = useDispatch();
	const toast = useToast();

	// Pagination calculations
	const propertyNotificationsTotal = notifications.length;
	const propertyNotificationsPages = Math.ceil(propertyNotificationsTotal / ITEMS_PER_PAGE);
	const propertyNotificationsStart = (propertyNotificationsPage - 1) * ITEMS_PER_PAGE;
	const propertyNotificationsEnd = propertyNotificationsStart + ITEMS_PER_PAGE;
	const paginatedPropertyNotifications = notifications.slice().reverse().slice(propertyNotificationsStart, propertyNotificationsEnd);

	const massNotificationsTotal = massNotifications.length;
	const massNotificationsPages = Math.ceil(massNotificationsTotal / ITEMS_PER_PAGE);
	const massNotificationsStart = (massNotificationsPage - 1) * ITEMS_PER_PAGE;
	const massNotificationsEnd = massNotificationsStart + ITEMS_PER_PAGE;
	const paginatedMassNotifications = massNotifications.slice().reverse().slice(massNotificationsStart, massNotificationsEnd);

	const handleSendNotification = () => {
		if (!notificationInformation.title || !notificationInformation.message) {
			toast({
				title: "Missing Information",
				description: "Please fill in both title and message fields",
				status: "warning",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		dispatch(sendMassNotificationsAsync({ notification: notificationInformation }))
			.then((r) => {
				if (r.error?.message) {
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				} else {
					toast({
						title: "Success!",
						description: "Mass notification sent!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setNotificationInformation(initialNotificationInformation);
				setValue("property");
			});
	};

	const handleNotificationClick = (notification, isMass) => {
		setSelectedNotification({ ...notification, isMass });
		onOpen();
	};

	const NotificationCard = ({ notification, isMass = false }) => (
		<Box
			p={4}
			borderWidth="1px"
			borderRadius="lg"
			borderColor={borderColor}
			bg={bgCard}
			_hover={{ bg: bgHover, cursor: "pointer" }}
			transition="all 0.2s"
			mb={3}
			onClick={() => handleNotificationClick(notification, isMass)}
		>
			<HStack justify="space-between">
				<VStack align="start" spacing={2}>
					<HStack>
						<Icon as={FiBell} color={isMass ? "purple.500" : "blue.500"} />
						<Text fontWeight="bold" fontSize="md">
							{notification.title}
						</Text>
						<Badge colorScheme={isMass ? "purple" : "blue"}>{isMass ? t("notifications.massNotification") : t("notifications.propertyNotification")}</Badge>
					</HStack>

					{!isMass && notification.address && (
						<HStack>
							<Icon as={FiHome} color="gray.500" />
							<Text fontSize="sm" color="gray.500">
								{notification.address}
							</Text>
						</HStack>
					)}

					<HStack>
						<Text fontSize="sm" color="gray.500">
							{moment(notification.timestamp).format(fullDateTimeFormat)}
						</Text>
					</HStack>
				</VStack>
			</HStack>
		</Box>
	);

	const NotificationDetailsModal = () => {
		const [filteredRecipients, setFilteredRecipients] = useState(null);
		const [showAllRecipients, setShowAllRecipients] = useState(false);

		// Reset states when modal opens/notification changes
		useEffect(() => {
			if (selectedNotification?.recipients && Array.isArray(selectedNotification.recipients)) {
				setFilteredRecipients(selectedNotification.recipients);
				setShowAllRecipients(false);
			}
		}, []);

		const currentRecipients = filteredRecipients || [];
		const totalRecipients = currentRecipients.length;
		const displayedRecipients = showAllRecipients ? currentRecipients : currentRecipients.slice(0, 5);
		const hiddenCount = totalRecipients - displayedRecipients.length;

		return (
			<Modal isOpen={isOpen} onClose={onClose} size="2xl">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>
						<HStack spacing={3}>
							<Icon as={FiBell} color={selectedNotification?.isMass ? "purple.500" : "blue.500"} />
							<Text>{selectedNotification?.title}</Text>
						</HStack>

						<HStack justify="flex-end" spacing={2} mt={2}>
							<Icon as={FiClock} color="gray.500" w={3} h={3} />
							<Text color="gray.500" fontSize="sm" fontWeight="normal">
								{moment(selectedNotification?.timestamp).format(fullDateTimeFormat)}
							</Text>
						</HStack>
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody pb={6}>
						<VStack align="stretch" spacing={4}>
							{selectedNotification?.address && (
								<HStack>
									<Icon as={FiHome} color="gray.500" />
									<Text color="gray.700">{selectedNotification.address}</Text>
								</HStack>
							)}

							<Box>
								<Text fontWeight="bold" mb={2}>
									{t("notifications.message")}
								</Text>
								<Text color="gray.700" whiteSpace={"break-spaces"}>
									{selectedNotification?.text || selectedNotification?.message}
								</Text>
							</Box>

							{selectedNotification?.recipients && (
								<Box>
									<Text fontWeight="bold">
										{t("notifications.recipients")} ({totalRecipients})
									</Text>

									{Array.isArray(selectedNotification.recipients) && (
										<>
											<Input
												placeholder={t("notifications.searchRecipients")}
												mb={3}
												size="sm"
												onChange={(e) => {
													const searchTerm = e.target.value.toLowerCase();
													const filtered = selectedNotification.recipients.filter((r) => r.toLowerCase().includes(searchTerm));
													setFilteredRecipients(filtered);
												}}
												leftIcon={<Icon as={FiSearch} color="gray.500" />}
											/>
											<VStack align="stretch" spacing={2}>
												<Wrap>
													{displayedRecipients.map((recipient, index) => (
														<WrapItem key={index} p={2} bg="gray.50" borderRadius="md">
															<Icon as={FiUsers} color="gray.500" mr={1} />
															<Text
																color="gray.700"
																fontSize="sm"
																flex={1}
																onClick={() => {
																	navigator.clipboard.writeText(recipient);
																	toast({
																		title: t("misc.copied"),
																		status: "success",
																		duration: 2000,
																	});
																}}
															>
																{recipient}
															</Text>
														</WrapItem>
													))}
												</Wrap>

												{hiddenCount > 0 && !showAllRecipients && (
													<Button
														size="sm"
														variant="ghost"
														rightIcon={<Icon as={FiChevronDown} />}
														onClick={() => setShowAllRecipients(true)}
														width="100%"
														bg="gray.50"
													>
														{t("notifications.showMore")}
													</Button>
												)}
												<Button
													size="sm"
													leftIcon={<Icon as={FiCopy} />}
													variant="ghost"
													onClick={() => {
														const emails = currentRecipients.join(", ");
														navigator.clipboard.writeText(emails);
														toast({
															title: t("notifications.emailsCopied"),
															status: "success",
															duration: 2000,
														});
													}}
												>
													{t("notifications.copyAllEmails")}
												</Button>
											</VStack>
										</>
									)}
									{!Array.isArray(selectedNotification.recipients) && (
										<HStack p={2} bg="gray.50" borderRadius="md">
											<Icon as={FiUsers} color="gray.500" />
											<Text color="gray.700" fontSize="sm">
												{selectedNotification.recipients.type === "property" ? t("notifications.propertyRecipients") : t("notifications.massRecipients")}
											</Text>
										</HStack>
									)}
								</Box>
							)}
						</VStack>
					</ModalBody>
				</ModalContent>
			</Modal>
		);
	};

	return (
		<Flex direction="column" gap={6}>
			<Card>
				<CardHeader>
					<HStack spacing={3}>
						<Icon as={FiSend} w={6} h={6} color="blue.500" />
						<Text fontSize="2xl" color={textColor} fontWeight="bold">
							{t("notifications.sendNotification")}
						</Text>
					</HStack>
				</CardHeader>
				<CardBody>
					<Stack direction={"column"} width={{ sm: "100%", md: "60%" }} spacing={4}>
						<FormControl isRequired>
							<FormLabel fontWeight="medium">{t("notifications.title")}</FormLabel>
							<Input
								type="text"
								placeholder={t("notifications.enterTitle")}
								name="title"
								value={notificationInformation.title}
								onChange={(e) =>
									setNotificationInformation({
										...notificationInformation,
										title: e.target.value,
									})
								}
							/>
						</FormControl>

						<FormControl isRequired>
							<FormLabel fontWeight="medium">{t("notifications.message")}</FormLabel>
							<Textarea
								placeholder={t("notifications.enterMessage")}
								name="message"
								value={notificationInformation.message}
								onChange={(e) =>
									setNotificationInformation({
										...notificationInformation,
										message: e.target.value,
									})
								}
								minH="120px"
							/>
						</FormControl>

						<FormControl>
							<FormLabel fontWeight="medium">{t("notifications.recipientType")}</FormLabel>
							<RadioGroup
								onChange={(newValue) => {
									setValue(newValue);
									setNotificationInformation({
										...notificationInformation,
										recipients: {
											type: "users",
											notifyOwners: false,
											notifyTenants: false,
										},
									});
								}}
								value={value}
							>
								<Stack direction="row" spacing={6}>
									<Radio value="property" colorScheme="blue">
										{t("notifications.singleProperty")}
									</Radio>
									<Radio value="users" colorScheme="blue">
										{t("notifications.multipleUsers")}
									</Radio>
								</Stack>
							</RadioGroup>
						</FormControl>

						{value === "property" ? (
							<Stack spacing={4}>
								<FormControl isRequired>
									<FormLabel fontWeight="medium">{t("notifications.selectProperty")}</FormLabel>
									<Select
										value={notificationInformation.recipients.propertyId}
										placeholder={t("notifications.chooseProperty")}
										onChange={(e) => {
											setNotificationInformation({
												...notificationInformation,
												recipients: {
													type: "property",
													propertyId: e.target.value,
													notifyOwner: false,
												},
											});
										}}
									>
										{properties
											?.filter((p) => p.tenants.filter((t) => !t.former).length !== 0)
											.map((p, index) => (
												<option value={p.id} key={index}>
													{getPropertyAddress(p)}
												</option>
											))}
									</Select>
								</FormControl>
								<Checkbox
									colorScheme="blue"
									isChecked={notificationInformation.recipients.notifyOwner}
									onChange={(e) => {
										setNotificationInformation({
											...notificationInformation,
											recipients: {
												...notificationInformation.recipients,
												notifyOwner: e.target.checked,
											},
										});
									}}
								>
									{t("notifications.includeOwner")}
								</Checkbox>
							</Stack>
						) : (
							<Stack spacing={4}>
								<Checkbox
									colorScheme="blue"
									onChange={(e) => {
										setNotificationInformation({
											...notificationInformation,
											recipients: {
												...notificationInformation.recipients,
												notifyOwners: e.target.checked,
											},
										});
									}}
								>
									{t("notifications.allOwners")}
								</Checkbox>
								<Checkbox
									colorScheme="blue"
									onChange={(e) => {
										setNotificationInformation({
											...notificationInformation,
											recipients: {
												...notificationInformation.recipients,
												notifyTenants: e.target.checked,
											},
										});
									}}
								>
									{t("notifications.allTenants")}
								</Checkbox>
							</Stack>
						)}

						<Button mt={4} colorScheme="blue" width={"fit-content"} onClick={handleSendNotification} leftIcon={<FiSend />} size="lg">
							{t("notifications.send")}
						</Button>
					</Stack>
				</CardBody>
			</Card>

			<Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap={6}>
				<Card>
					<CardHeader>
						<HStack spacing={3} justify="space-between">
							<HStack spacing={3}>
								<Icon as={FiBell} w={6} h={6} color="blue.500" />
								<Text fontSize="xl" color={textColor} fontWeight="bold">
									{t("notifications.propertyNotifications")}
								</Text>
							</HStack>
						</HStack>
					</CardHeader>
					<CardBody>
						<VStack align="stretch" spacing={4}>
							{notifications.length > 0 ? (
								<>
									{paginatedPropertyNotifications.map((notification, index) => (
										<NotificationCard key={index} notification={notification} />
									))}
									<TablePagination
										currentPage={propertyNotificationsPage}
										totalPages={propertyNotificationsPages}
										totalItems={propertyNotificationsTotal}
										onPageChange={setPropertyNotificationsPage}
										itemsName={t("notifications.notifications")}
									/>
								</>
							) : (
								<Text color="gray.500">{t("notifications.noPropertyNotifications")}</Text>
							)}
						</VStack>
					</CardBody>
				</Card>

				<Card>
					<CardHeader>
						<HStack spacing={3} justify="space-between">
							<HStack spacing={3}>
								<Icon as={FiBell} w={6} h={6} color="purple.500" />
								<Text fontSize="xl" color={textColor} fontWeight="bold">
									{t("notifications.massNotifications")}
								</Text>
							</HStack>
						</HStack>
					</CardHeader>
					<CardBody>
						<VStack align="stretch" spacing={4}>
							{massNotifications.length > 0 ? (
								<>
									{paginatedMassNotifications.map((notification, index) => (
										<NotificationCard key={index} notification={notification} isMass={true} />
									))}
									<TablePagination
										currentPage={massNotificationsPage}
										totalPages={massNotificationsPages}
										totalItems={massNotificationsTotal}
										onPageChange={setMassNotificationsPage}
										itemsName={t("notifications.notifications")}
									/>
								</>
							) : (
								<Text color="gray.500">{t("notifications.noMassNotifications")}</Text>
							)}
						</VStack>
					</CardBody>
				</Card>
			</Grid>

			<NotificationDetailsModal />
		</Flex>
	);
}
